import {UploadResult} from '../types.ts';
import joinPath from '../../lib/joinPath.ts';

/*
 * Uploads one file in chunks (ChunkedUploadService on the server): start a
 * session, send 8 MB chunks a few at a time with retries, commit, and if the
 * file needs unpacking or converting, poll the job until it is done. Each
 * request is short and small, which is what gets a large file through
 * Cloudflare's body and response limits, and a dropped connection costs one
 * chunk rather than the whole file.
 */

export type UploadStage = 'uploading' | 'processing' | 'done' | 'failed';

export type UploadProgress = {
    sentBytes: number;
    totalBytes: number;
    stage: UploadStage;
};

export type ChunkedUploadOptions = {
    file: File;
    containerName?: string | null;
    folderPath?: string | null;
    apiServiceBaseUri: string;
    getAccessToken: () => Promise<string>;
    onProgress?: (progress: UploadProgress) => void;
    signal?: AbortSignal;
};

type Started = {
    session: string;
    chunkBytes: number;
    chunkCount: number;
    expiresAt: string;
};

type Committed = {
    file: UploadResult;
    processingJobId: string | null;
};

type JobStatus = {
    state: 'pending' | 'running' | 'done' | 'failed';
    files: UploadResult[] | null;
    error: string | null;
};

export class UploadError extends Error {
    constructor(
        message: string,
        public readonly status?: number
    ) {
        super(message);
        this.name = 'UploadError';
    }
}

const PARALLEL_CHUNKS = 3;
const ATTEMPTS_PER_CHUNK = 4;
const POLL_INTERVAL_MS = 1500;

const abortError = () => new DOMException('The upload was cancelled.', 'AbortError');

const throwIfAborted = (signal?: AbortSignal) => {
    if (signal?.aborted) {
        throw abortError();
    }
};

// What the API said went wrong: {message} or, from a service result, {errors: [{message}]}.
const messageIn = (text: string, fallback: string): string => {
    try {
        const data = JSON.parse(text);
        return data?.message ?? data?.errors?.[0]?.message ?? fallback;
    } catch {
        return fallback;
    }
};

const delay = (milliseconds: number, signal?: AbortSignal) =>
    new Promise<void>((resolve, reject) => {
        const timer = setTimeout(() => {
            signal?.removeEventListener('abort', onAbort);
            resolve();
        }, milliseconds);
        const onAbort = () => {
            clearTimeout(timer);
            reject(abortError());
        };
        signal?.addEventListener('abort', onAbort, {once: true});
    });

async function requestJson<T>(
    method: 'GET' | 'POST',
    url: string,
    body: unknown,
    getAccessToken: () => Promise<string>,
    signal?: AbortSignal
): Promise<T> {
    const response = await fetch(url, {
        method,
        headers: {
            Authorization: `bearer ${await getAccessToken()}`,
            ...(body !== undefined ? {'Content-Type': 'application/json'} : {})
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal
    });

    if (!response.ok) {
        throw new UploadError(
            messageIn(await response.text(), `The upload failed (${response.status}).`),
            response.status
        );
    }

    return (await response.json()) as T;
}

// One chunk, as the raw body of a PUT. XMLHttpRequest because fetch reports no upload progress.
function putChunk(
    url: string,
    chunk: Blob,
    token: string,
    onProgress: (sentBytes: number) => void,
    signal?: AbortSignal
): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const onAbort = () => xhr.abort();

        xhr.open('PUT', url);
        xhr.setRequestHeader('Authorization', `bearer ${token}`);
        xhr.setRequestHeader('Content-Type', 'application/octet-stream');
        xhr.upload.onprogress = event => onProgress(event.loaded);
        xhr.onload = () => {
            signal?.removeEventListener('abort', onAbort);
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
            } else {
                reject(
                    new UploadError(
                        messageIn(xhr.responseText, `Chunk upload failed (${xhr.status}).`),
                        xhr.status
                    )
                );
            }
        };
        xhr.onerror = () => {
            signal?.removeEventListener('abort', onAbort);
            reject(new UploadError('The connection was lost.', 0));
        };
        xhr.onabort = () => {
            signal?.removeEventListener('abort', onAbort);
            reject(abortError());
        };
        signal?.addEventListener('abort', onAbort, {once: true});
        xhr.send(chunk);
    });
}

// Worth another go: the connection dropped, or the server or something in front of it failed.
const isTransient = (error: unknown) =>
    error instanceof UploadError && (error.status === 0 || error.status === undefined || error.status >= 500);

export async function uploadFileInChunks(options: ChunkedUploadOptions): Promise<UploadResult[]> {
    const {file, apiServiceBaseUri, getAccessToken, onProgress, signal} = options;
    const sent: number[] = [];

    const report = (stage: UploadStage) =>
        onProgress?.({
            sentBytes: Math.min(
                file.size,
                sent.reduce((total, bytes) => total + bytes, 0)
            ),
            totalBytes: file.size,
            stage
        });

    throwIfAborted(signal);
    report('uploading');

    const started = await requestJson<Started>(
        'POST',
        joinPath(apiServiceBaseUri, 'api/uploads/sessions'),
        {
            containerName: options.containerName ?? null,
            folderPath: options.folderPath ?? null,
            fileName: file.name,
            contentType: file.type || null,
            totalBytes: file.size
        },
        getAccessToken,
        signal
    );

    const sessionPath = `api/uploads/sessions/${encodeURIComponent(started.session)}`;

    for (let index = 0; index < started.chunkCount; index++) {
        sent[index] = 0;
    }

    const sendChunk = async (index: number) => {
        const chunk = file.slice(index * started.chunkBytes, Math.min(file.size, (index + 1) * started.chunkBytes));
        const url = joinPath(apiServiceBaseUri, `${sessionPath}/chunks/${index}`);

        for (let attempt = 1; ; attempt++) {
            throwIfAborted(signal);
            try {
                await putChunk(
                    url,
                    chunk,
                    await getAccessToken(),
                    bytes => {
                        sent[index] = Math.min(bytes, chunk.size);
                        report('uploading');
                    },
                    signal
                );
                sent[index] = chunk.size;
                report('uploading');
                return;
            } catch (error) {
                sent[index] = 0;
                if (attempt >= ATTEMPTS_PER_CHUNK || !isTransient(error)) {
                    throw error;
                }
                await delay(1000 * 2 ** (attempt - 1), signal);
            }
        }
    };

    // A few chunks in flight at once; each worker takes the next index until none are left.
    let next = 0;
    const worker = async () => {
        while (next < started.chunkCount) {
            await sendChunk(next++);
        }
    };
    await Promise.all(Array.from({length: Math.min(PARALLEL_CHUNKS, started.chunkCount)}, worker));

    const committed = await requestJson<Committed>(
        'POST',
        joinPath(apiServiceBaseUri, `${sessionPath}/commit`),
        undefined,
        getAccessToken,
        signal
    );

    if (!committed.processingJobId) {
        report('done');
        return [committed.file];
    }

    // Unpacking or converting runs as a job; the file list comes from it.
    report('processing');

    for (;;) {
        await delay(POLL_INTERVAL_MS, signal);

        const status = await requestJson<JobStatus>(
            'GET',
            joinPath(apiServiceBaseUri, `api/uploads/jobs/${encodeURIComponent(committed.processingJobId)}`),
            undefined,
            getAccessToken,
            signal
        );

        if (status.state === 'done') {
            report('done');
            return status.files ?? [];
        }

        if (status.state === 'failed') {
            report('failed');
            throw new UploadError(status.error ?? 'The file could not be processed.');
        }
    }
}

// Every File in a FormData, for callers that still build one (name "file", any number of them).
export const filesIn = (data: FormData): File[] => {
    const files: File[] = [];
    data.forEach(value => {
        if (value instanceof File) {
            files.push(value);
        }
    });
    return files;
};
